
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Workflow,
  Save, 
  ArrowLeft, 
  Play, 
  Calendar, 
  Activity, 
  AlertCircle,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  fetchWorkflowById, 
  updateWorkflow, 
  executeWorkflow 
} from '@/services/agentic/workflowService';
import { WorkflowEditor } from '@/components/agentic/workflows';
import { Node, Edge } from '@xyflow/react';
import { NodeData, EdgeData } from '@/components/agentic/agents/types';
import { AgentWorkflow } from '@/types/agentic';
import { Skeleton } from '@/components/ui/skeleton';

const EditWorkflowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<AgentWorkflow | null>(null);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [nodes, setNodes] = useState<Node<NodeData>[]>([]);
  const [edges, setEdges] = useState<Edge<EdgeData>[]>([]);

  useEffect(() => {
    const loadWorkflow = async () => {
      if (!id) return;
      
      try {
        const workflowData = await fetchWorkflowById(id);
        if (workflowData) {
          setWorkflow(workflowData);
          setWorkflowName(workflowData.name);
          setWorkflowDescription(workflowData.description || '');
          setIsActive(workflowData.is_active);
          
          // Load canvas data
          if (workflowData.canvas_data) {
            const canvasData = typeof workflowData.canvas_data === 'string'
              ? JSON.parse(workflowData.canvas_data)
              : workflowData.canvas_data;
            
            setNodes(canvasData.nodes || []);
            setEdges(canvasData.edges || []);
          }
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        toast.error('Failed to load workflow');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadWorkflow();
  }, [id]);

  const handleSaveWorkflow = async (updatedNodes?: Node<NodeData>[], updatedEdges?: Edge<EdgeData>[]) => {
    if (!workflow || !id) return;
    
    setIsSaving(true);
    
    try {
      const nodesToSave = updatedNodes || nodes;
      const edgesToSave = updatedEdges || edges;
      
      const updatedWorkflow = await updateWorkflow(id, {
        name: workflowName,
        description: workflowDescription,
        is_active: isActive,
        canvas_data: {
          nodes: nodesToSave,
          edges: edgesToSave
        }
      });
      
      setWorkflow(updatedWorkflow);
      toast.success('Workflow saved successfully');
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNodeAndEdgeChange = (updatedNodes: Node<NodeData>[], updatedEdges: Edge<EdgeData>[]) => {
    setNodes(updatedNodes);
    setEdges(updatedEdges);
    handleSaveWorkflow(updatedNodes, updatedEdges);
  };

  const handleExecuteWorkflow = async () => {
    if (!workflow) return;
    
    try {
      await executeWorkflow(workflow.id);
      toast.success('Workflow execution started');
    } catch (error) {
      console.error('Error executing workflow:', error);
      toast.error('Failed to execute workflow');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-1/4" />
        </div>
        <Skeleton className="h-[70vh] w-full" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
          <h2 className="text-2xl font-bold mb-2">Workflow Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The workflow you are looking for does not exist or has been deleted.
          </p>
          <Button onClick={() => navigate('/agentic/workflows')}>
            Return to Workflows
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/agentic/workflows')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Workflow</h1>
            <p className="text-muted-foreground">
              {workflow.trigger_type === 'manual' && (
                <span className="flex items-center gap-1 text-sm">
                  <Activity className="h-3 w-3" />
                  Manual Trigger
                </span>
              )}
              {workflow.trigger_type === 'scheduled' && (
                <span className="flex items-center gap-1 text-sm">
                  <Calendar className="h-3 w-3" />
                  Scheduled Trigger
                </span>
              )}
              {workflow.trigger_type === 'event' && (
                <span className="flex items-center gap-1 text-sm">
                  <AlertCircle className="h-3 w-3" />
                  Event Trigger
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => handleSaveWorkflow()}
            disabled={isSaving}
            className="flex items-center gap-1"
          >
            <Save className="h-4 w-4" />
            Save
          </Button>
          {isActive && (
            <Button 
              onClick={handleExecuteWorkflow}
              className="flex items-center gap-1"
            >
              <Play className="h-4 w-4" />
              Execute
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Workflow className="h-5 w-5 text-primary" />
                Workflow Canvas
              </CardTitle>
              <CardDescription>
                Connect nodes to define your workflow execution path
              </CardDescription>
            </CardHeader>
            <CardContent>
              <WorkflowEditor
                initialNodes={nodes}
                initialEdges={edges}
                onSave={handleNodeAndEdgeChange}
              />
            </CardContent>
          </Card>
        </div>
        
        <div className="xl:col-span-1">
          <Card className="mb-6 sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Workflow Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="workflow-name">Name</Label>
                <Input
                  id="workflow-name"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="workflow-description">Description</Label>
                <Textarea
                  id="workflow-description"
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between space-y-0 pt-2">
                <Label htmlFor="workflow-active">Active</Label>
                <Switch
                  id="workflow-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                onClick={() => handleSaveWorkflow()}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="flex items-center gap-1">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Save className="h-4 w-4" />
                    Save Settings
                  </span>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditWorkflowPage;
